/* Drop-in replacement: checklist routing must run before array routing. */
import React from "react";
import { getValue } from "../../utils";
import { EmptyField } from "./components/FieldShell";
import ArrayField from "./fields/ArrayField";
import {
  BooleanField,
  DropdownField,
  GeolocationField,
  PrimitiveField,
  SignatureField,
} from "./fields/BasicFields";
import { FileField, ImageLinkField } from "./fields/FileFields";
import {
  GenericSubtableField,
  PermitChecklistField,
} from "./fields/SubtableFields";
import { getBaseField, isEmpty, isTrue } from "./utils/valueUtils";

const CHECKLIST_TYPES = new Set(["permitchecklists", "safetychecks"]);

function isChecklistColumn(column) {
  const type = String(column?.type || "").toLowerCase();
  const subFields = Array.isArray(column?.subFields) ? column.subFields : [];

  if (CHECKLIST_TYPES.has(type)) return true;

  return subFields.some((subField) =>
    CHECKLIST_TYPES.has(String(subField?.type || "").toLowerCase()),
  );
}

export default function FieldRenderer({ row, column, index }) {
  const label = column.label || column.field || `Field ${index + 1}`;
  const raw = getValue(row, column.field);
  const type = String(column.type || "").toLowerCase();
  const key = `${column.field}-${index}`;

  if (type === "imagelink") {
    return <ImageLinkField key={key} label={label} column={column} raw={raw} />;
  }

  if (type === "signature") {
    return <SignatureField key={key} label={label} column={column} raw={raw} />;
  }

  if (type === "dropdown") {
    return <DropdownField key={key} row={row} label={label} column={column} />;
  }

  if (type === "file") {
    return <FileField key={key} label={label} column={column} raw={raw} />;
  }

  /*
   * This must run before the array branch. It supports either:
   *   type: "safetychecks"
   * or a subString column containing a permitchecklists subField.
   */
  if (isChecklistColumn(column)) {
    return (
      <PermitChecklistField key={key} row={row} label={label} column={column} />
    );
  }

  if (type === "substring") {
    return (
      <GenericSubtableField key={key} row={row} label={label} column={column} />
    );
  }

  if (type === "geolocation") {
    return (
      <GeolocationField key={key} label={label} column={column} raw={raw} />
    );
  }

  if (type === "boolean" || typeof raw === "boolean") {
    return (
      <BooleanField key={key} label={label} column={column} raw={isTrue(raw)} />
    );
  }

  const baseValue = row?.[getBaseField(column.field)];
  const isArrayField =
    (type === "checkbox" && isTrue(column.array)) ||
    Array.isArray(raw) ||
    Array.isArray(baseValue);

  if (isArrayField) {
    return (
      <ArrayField key={key} row={row} label={label} column={column} raw={raw} />
    );
  }

  if (isEmpty(raw)) {
    return <EmptyField key={key} label={label} column={column} />;
  }

  return <PrimitiveField key={key} label={label} column={column} raw={raw} />;
}
